import { Component, Element, Event, EventEmitter, Host, Prop, h } from '@stencil/core';

export interface AhaIconPicker extends HTMLAhaIconPickerElement {}

const ICONS = [
  'fa-grid-2',
  'fa-diamond',
  'fa-sparkle',
  'fa-heart',
  'fa-bookmark',
  'fa-certificate',
  'fa-star',
  'fa-rocket-launch',
  'fa-bolt',
  'fa-gem',
  'fa-layer-group',
  'fa-gift',
  'fa-flask',
  'fa-chart-simple',
  'fa-memo-pad',
  'fa-database',
  'fa-book',
  'fa-square-question',
  'fa-landmark',
  'fa-hashtag',
  'fa-paperclip',
  'fa-recycle',
  'fa-tag',
  'fa-circle-info',
  'fa-bell',
  'fa-bullhorn',
  'fa-megaphone',
  'fa-message-lines',
  'fa-comments-question',
  'fa-thumbs-up',
  'fa-palette',
  'fa-pen',
  'fa-image',
  'fa-pen-circle',
  'fa-paper-plane',
  'fa-film',
  'fa-universal-access',
  'fa-person',
  'fa-people-pants-simple',
  'fa-family-pants',
  'fa-ghost',
  'fa-user-bounty-hunter',
  'fa-globe',
  'fa-bullseye-pointer',
  'fa-bullseye',
  'fa-crosshairs-simple',
  'fa-eye',
  'fa-aperture',
  'fa-bug',
  'fa-alien-8bit',
  'fa-cloud',
  'fa-user-secret',
  'fa-lock',
  'fa-server',
  'fa-command',
  'fa-drone',
  'fa-webhook',
  'fa-code',
  'fa-signal-stream',
  'fa-wifi',
  'fa-laptop-code',
  'fa-screencast',
  'fa-tablet',
  'fa-signal',
  'fa-power-off',
  'fa-gear',
  'fa-starship-freighter',
  'fa-starfighter-twin-ion-engine-advanced',
  'fa-jedi',
  'fa-crystal-ball',
  'fa-swords',
  'fa-hurricane',
  'fa-comet',
  'fa-galaxy',
  'fa-fire',
  'fa-sun',
  'fa-star-shooting',
  'fa-asterisk',
];

/**
 * A component to make selecting icons easy.
 */
@Component({
  tag: 'aha-icon-picker',
  styleUrl: 'aha-icon-picker.css',
  shadow: false,
  scoped: true,
})
export class AhaIconPicker {
  @Element() host!: HTMLAhaIconPickerElement;

  private menuContentElem?: HTMLAhaMenuContentElement;

  /**
   * Triggers when the value is changed.
   */
  @Event({
    eventName: 'aha-icon-picker:change',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  changeEvent!: EventEmitter;

  /**
   * Triggers when the icon picker menu is initially opened but before it is visible.
   */
  @Event({
    eventName: 'aha-icon-picker:open',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openEvent!: EventEmitter;

  /**
   *
   * Triggers when the icon-picker menu is visible on the page.
   */
  @Event({
    eventName: 'aha-icon-picker:opened',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  openedEvent!: EventEmitter;

  /**
   * Triggers when the icon picker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-icon-picker:close',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closeEvent!: EventEmitter;

  /**
   * Triggers when the icon picker menu is no longer visible on the page.
   */
  @Event({
    eventName: 'aha-icon-picker:closed',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  closedEvent!: EventEmitter;

  /**
   * Triggers when the icon picker menu is initially closed but before it is hidden.
   */
  @Event({
    eventName: 'aha-icon-picker:cancel',
    cancelable: true,
    bubbles: true,
    composed: true,
  }) //eslint-disable-line @stencil/decorators-style
  cancelEvent!: EventEmitter;

  /** The name of the form control that will be submitted via a regular `<form>` submit. */
  @Prop({ reflect: true }) name: string | undefined;

  /** The selected icon. */
  @Prop({ reflect: true }) value: string = '';

  /** Wether or not the icon picker is visible or not. */
  @Prop({ reflect: true }) open: boolean = false;

  /** Whether or not the `value` of the component can be set, or if it can only be read. */
  @Prop({ reflect: true, attribute: 'readonly' }) readOnly?: boolean;

  /** Whether or not the component is disabled. */
  @Prop({ reflect: true }) disabled?: boolean;

  connectedCallback() {}

  componentDidLoad() {
    // If the embedded menu is being opened, then ensure the icon picker is also opened.
    this.menuContentElem?.addEventListener('aha-menu-content:open', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.openEvent.emit(this.host);
    });

    // If the embedded menu is fully opened, fire off the opened event for the icon picker
    this.menuContentElem?.addEventListener('aha-menu-content:opened', e => {
      this.openedEvent.emit(this.host);
    });

    // If the embedded menu is being closed, then ensure the icon picker is also closed.
    this.menuContentElem?.addEventListener('aha-menu-content:close', e => {
      const menuContent = e.target as HTMLAhaMenuContentElement;
      this.open = menuContent.open;
      this.closeEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:closed', e => {
      this.closedEvent.emit(this.host);
    });

    // If the embedded menu is closed, then fire the closed event.
    this.menuContentElem?.addEventListener('aha-menu-content:cancel', (event: any) => {
      if (event.detail) {
        // @ts-ignore
        const menu = window.portalClosest(event.detail, 'aha-menu');
        if (!menu) return;
      }
      this.cancelEvent.emit(this.host);
    });
  }

  handleClick(event: Event, icon: string) {
    this.value = icon;
    this.open = false;
    this.changeEvent.emit(this.host);
  }

  render() {
    return (
      <Host>
        <slot name="form_control">
          {/* The hidden field that is used to pass the value to any parent <form> element. */}
          <input
            type="hidden"
            value={this.value}
            name={this.name}
            readOnly={this.readOnly}
            disabled={this.disabled}
          />
        </slot>
        <aha-menu open={this.open} disabled={this.readOnly || this.disabled}>
          <aha-button
            slot="control"
            disabled={this.disabled}
            onClick={() => (this.open = !this.open)}
          >
            <div class="tw-flex tw-items-center tw-gap-1 tw-p-05">
              <aha-icon icon={`fa-solid ${this.value}`}></aha-icon>
              <aha-icon icon="fa-solid fa-caret-down"></aha-icon>
            </div>
          </aha-button>
          <aha-menu-content
            unrestricted-scroll
            ref={ref => (this.menuContentElem = ref)}
            style={{
              // Add extra padding between the popup and the triggering element to accomodate the
              // triggering elements focus state border
              '--aha-menu-content--vertical-margin': '2px',
              '--aha-menu-content--font-size': '14px',
            }}
          >
            <div class="tw-flex tw-flex-col tw-gap-2 tw-px-3">
              <div class="tw-grid tw-grid-cols-6">
                {ICONS.map(icon => (
                  <div
                    class="pickable-icon tw-cursor-pointer tw-text-center tw-p-1"
                    onClick={event => this.handleClick(event, icon)}
                  >
                    <aha-icon icon={`fa-solid ${icon}`}></aha-icon>
                  </div>
                ))}
              </div>
            </div>
          </aha-menu-content>
        </aha-menu>
      </Host>
    );
  }
}
